import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getEnvironment } from "./utils/getEnvironment";
import { RouterProvider } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/uk";
import { ThemeProvider, createTheme } from "@mui/material";
import router from "./router";
import { useCookies } from "react-cookie";
import useWeglot from "./hooks/useWeglot";
import AppWrapper from "./components/AppWrapper";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import { initMetaPixel } from "./utils/metaPixel";

const environment = getEnvironment();

const Main = () => {
    useWeglot();

    useEffect(() => {
        initMetaPixel();
    }, []);

    const [cookies, setCookie, removeCookie] = useCookies([
        "theme_color",
        "preferredLanguage",
    ]);

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    primary: {
                        main: cookies.theme_color ?? "#3B2F80",
                    },
                    secondary: {
                        main: "#FFFFFF",
                    },
                    info: {
                        main: (cookies.theme_color ?? "#3B2F80") + "33",
                    },
                    warning: {
                        main: "#141414",
                    },
                },
                shadows: [
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                    "none",
                ],
            }),
        [cookies]
    );

    // Language detection and setting
    useEffect(() => {
        //const supportedLanguages = ["en", "ru", "uk", "pl", "es"];

        const ensureSwitch = (lang: string) => {
            if (!window.Weglot) return;
            const doSwitch = () => {
                const current = window.Weglot!.getCurrentLang?.();
                if (current !== lang) window.Weglot!.switchTo(lang);
            };
            (window as any).__weglotReady
                ? doSwitch()
                : window.Weglot.on?.("initialized", doSwitch);
        };

        // 1) If we already have a preference → use it and stop.
        const preferred = cookies.preferredLanguage;
        if (preferred) {
            ensureSwitch(preferred);
            removeCookie("preferredLanguage", { path: "/" });
        }
    }, [cookies.preferredLanguage, setCookie, removeCookie]);

    return (
        <ThemeProvider theme={theme}>
            <SnackbarProvider>
                <AppWrapper>
                    <RouterProvider router={router} />
                </AppWrapper>
            </SnackbarProvider>
        </ThemeProvider>
    );
};

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
);
root.render(
    <>
        <GoogleOAuthProvider clientId={environment.google_client_id}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="uk">
                <React.StrictMode>
                    <Main />
                </React.StrictMode>
            </LocalizationProvider>
        </GoogleOAuthProvider>
    </>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
